/**
 * Вкладка «Загрузка и выгрузка» каталога программ обучения (training-catalog-v2; ТЗ 26.10.2026,
 * п.4.4 и 4.6; ответ заказчицы 27.10, В10 — выгрузка для Битрикса). Выгрузка — Excel в формате
 * каталога заказчицы (лист программ и лист разделов). Загрузка — сначала предпросмотр: сервер
 * выполняет загрузку целиком и откатывает её, поэтому предпросмотр точно совпадает с итогом;
 * затем «Загрузить каталог» с подтверждением. Строки с ошибками не загружаются — загрузка файла
 * с ошибками только с явным согласием. Правит ТОЛЬКО админ.
 */
import { useState } from 'react'
import { Download, FileSpreadsheet, Upload } from 'lucide-react'
import { adminApi, type CatalogImportResult, type CatalogProgramChange } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { downloadBlob } from '@/lib/download'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Field } from '@/design-system/components/primitives/field'
import { FileInput } from '@/design-system/components/primitives/file-input'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

const EXPORT_FILE_NAME = 'Каталог программ.xlsx'

/** Число и слово: 1 программа, 2 программы, 5 программ. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} ${few}`
  return `${n} ${many}`
}

function ChangeList({ items, showFields }: { items: CatalogProgramChange[]; showFields: boolean }) {
  return (
    <ul className="divide-y divide-line">
      {items.map((c) => (
        <li key={c.id + c.code} className="py-2">
          <div className="text-14 text-txt">
            <span className="mr-2 text-txt-secondary">{c.code}</span>
            {c.name}
            {c.restored ? (
              <Badge variant="blue" className="ml-2 align-middle">
                Возвращается из архива
              </Badge>
            ) : null}
          </div>
          {showFields && c.fields.length > 0 ? (
            <ul className="mt-1 space-y-0.5 text-12 text-txt-secondary">
              {c.fields.map((f) => (
                <li key={f.field}>
                  <span className="font-medium text-txt">{f.field}:</span>{' '}
                  <span className="line-through">{f.old || '—'}</span> → {f.new || '—'}
                </li>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

/** Сворачиваемый блок предпросмотра со счётчиком. */
function Section({
  title,
  count,
  children,
  defaultOpen = false,
}: {
  title: string
  count: number
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  if (count === 0) return null
  return (
    <details className="border-t border-line py-2 first:border-t-0" open={defaultOpen}>
      <summary className="cursor-pointer select-none py-1 text-14 font-medium text-txt">
        {title}: {count}
      </summary>
      <div className="pt-1">{children}</div>
    </details>
  )
}

export function CatalogFileTab() {
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<CatalogImportResult | null>(null)
  const [result, setResult] = useState<CatalogImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [archiveMissing, setArchiveMissing] = useState(true)
  const [skipErrors, setSkipErrors] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  async function exportCatalog() {
    setExportError(null)
    setExporting(true)
    try {
      downloadBlob(EXPORT_FILE_NAME, await adminApi.training.exportCatalog())
    } catch (err) {
      setExportError(getErrorMessage(err, 'Не удалось выгрузить каталог'))
    } finally {
      setExporting(false)
    }
  }

  async function runPreview(f: File) {
    setError(null)
    setResult(null)
    setPreview(null)
    setSkipErrors(false)
    setBusy(true)
    try {
      setPreview(await adminApi.training.importCatalog(f, { apply: false, archiveMissing }))
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось прочитать файл каталога'))
    } finally {
      setBusy(false)
    }
  }

  async function apply() {
    if (!file) return
    setError(null)
    setBusy(true)
    try {
      setResult(await adminApi.training.importCatalog(file, { apply: true, archiveMissing, skipErrors }))
      setPreview(null)
      setFile(null)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось загрузить каталог'))
    } finally {
      setBusy(false)
      setConfirmOpen(false)
    }
  }

  const issues = preview?.issues ?? []
  const canApply = Boolean(preview && file) && (issues.length === 0 || skipErrors) && !busy

  return (
    <div className="flex flex-col gap-4">
      <Card header="Выгрузка каталога">
        <p className="mb-3 text-14 text-txt-secondary">
          Excel в формате каталога: лист программ (с ценами и разделом УЦ) и лист разделов. Файл можно поправить и
          загрузить обратно — так удобнее всего вести каталог и переносить его в Битрикс.
        </p>
        {exportError ? (
          <Alert variant="danger" className="mb-3">
            {exportError}
          </Alert>
        ) : null}
        <Button
          size="sm"
          variant="secondary"
          disabled={exporting}
          onClick={() => void exportCatalog()}
          leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
        >
          {exporting ? 'Формирование…' : 'Скачать каталог'}
        </Button>
      </Card>

      <Card header="Загрузка каталога">
        <p className="mb-3 text-14 text-txt-secondary">
          Программы сопоставляются по коду курса: новые добавляются, изменённые обновляются, повторная загрузка того же
          файла ничего не меняет. Поданные заказы не меняются — у них сохраняется программа на дату подачи.
        </p>
        <Field label="Файл каталога (.xlsx)" className="my-0 max-w-xl">
          {(props) => (
            <FileInput
              id={props.id}
              accept=".xlsx"
              value={file}
              buttonLabel="Выбрать файл"
              onChange={(f) => {
                setFile(f)
                if (f) void runPreview(f)
                else setPreview(null)
              }}
              disabled={busy}
            />
          )}
        </Field>
        <LabeledCheckbox checked={archiveMissing} onCheckedChange={setArchiveMissing} disabled={busy} className="mt-3">
          Убрать в архив программы, которых нет в файле
        </LabeledCheckbox>

        {busy && !preview ? <p className="mt-3 text-14 text-txt-secondary">Проверяем файл…</p> : null}
        {error ? (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        ) : null}

        {preview ? (
          <div className="mt-4">
            <h3 className="mb-2 flex items-center gap-2 text-15 font-semibold text-txt">
              <FileSpreadsheet className="size-4 stroke-current" aria-hidden />
              Предпросмотр: {file?.name}
            </h3>
            <ul className="mb-3 flex flex-wrap gap-2 text-13">
              <li><Badge variant="green">Новых: {preview.created.length}</Badge></li>
              <li><Badge variant="blue">Изменится: {preview.changed.length}</Badge></li>
              <li><Badge variant="gray">Без изменений: {preview.unchanged}</Badge></li>
              <li>
                <Badge variant={preview.missing.length ? 'amber' : 'gray'}>
                  Нет в файле: {preview.missing.length}
                </Badge>
              </li>
              <li><Badge variant={issues.length ? 'red' : 'gray'}>Строк с ошибками: {issues.length}</Badge></li>
            </ul>

            {issues.length > 0 ? (
              <div className="mb-3">
                <Alert variant="danger" className="mb-2">
                  {plural(issues.length, 'строка не будет загружена', 'строки не будут загружены', 'строк не будут загружены')}{' '}
                  — исправьте их в файле и выберите его снова, либо загрузите каталог без них.
                </Alert>
                <DataTable>
                  <DataTableHead>
                    <tr>
                      <DataTableHeaderCell>Строка</DataTableHeaderCell>
                      <DataTableHeaderCell>Код</DataTableHeaderCell>
                      <DataTableHeaderCell>Колонка</DataTableHeaderCell>
                      <DataTableHeaderCell>Что не так</DataTableHeaderCell>
                    </tr>
                  </DataTableHead>
                  <DataTableBody>
                    {issues.map((i, idx) => (
                      <DataTableRow key={`${i.sheet}-${i.row}-${idx}`}>
                        <DataTableCell className="whitespace-nowrap">
                          {i.sheet === 'sections' ? 'Разделы, ' : ''}
                          {i.row}
                        </DataTableCell>
                        <DataTableCell className="whitespace-nowrap">{i.code || '—'}</DataTableCell>
                        <DataTableCell>{i.column || '—'}</DataTableCell>
                        <DataTableCell>{i.message}</DataTableCell>
                      </DataTableRow>
                    ))}
                  </DataTableBody>
                </DataTable>
                <LabeledCheckbox checked={skipErrors} onCheckedChange={setSkipErrors} className="mt-2">
                  Загрузить без строк с ошибками
                </LabeledCheckbox>
              </div>
            ) : null}

            <Section title="Новые программы" count={preview.created.length}>
              <ChangeList items={preview.created} showFields={false} />
            </Section>
            <Section title="Изменённые программы" count={preview.changed.length} defaultOpen>
              <ChangeList items={preview.changed} showFields />
            </Section>
            <Section
              title={archiveMissing ? 'Уйдут в архив (нет в файле)' : 'Нет в файле (останутся как есть)'}
              count={preview.missing.length}
            >
              <ul className="divide-y divide-line">
                {preview.missing.map((m) => (
                  <li key={m.id} className="py-2 text-14 text-txt">
                    <span className="mr-2 text-txt-secondary">{m.code || 'без кода'}</span>
                    {m.name}
                  </li>
                ))}
              </ul>
              {archiveMissing ? (
                <p className="mt-1 text-12 text-txt-secondary">
                  Архивные программы остаются в истории и в журнале; их записи можно перенести на новые программы на
                  вкладке «Сопоставление».
                </p>
              ) : null}
            </Section>
            <Section title="Новые разделы" count={preview.sectionsCreated.length}>
              <ul className="list-disc pl-5 text-14 text-txt">
                {preview.sectionsCreated.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </Section>
            <Section title="Пустые разделы удалятся" count={preview.sectionsRemoved.length}>
              <ul className="list-disc pl-5 text-14 text-txt">
                {preview.sectionsRemoved.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </Section>
            {/* Пакеты (training-packages-approval): состав и цену файл не задаёт — загрузка их не
                меняет, только показывает, каких пакетов из файла ещё нет в кабинете. */}
            {preview.packages.length > 0 ? (
              <div className="mt-3">
                <p className="text-13 text-txt-secondary">
                  Пакеты программ в файле — состав и цена задаются на вкладке «Пакеты», загрузка их не меняет:
                </p>
                <ul className="mt-1 list-disc pl-5 text-14 text-txt">
                  {preview.packages.map((pk) => (
                    <li key={pk.name}>
                      {pk.name}
                      <span className="text-txt-secondary">
                        {pk.exists ? ' — есть в кабинете' : ' — нет в кабинете, добавьте на вкладке «Пакеты»'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={!canApply}
                onClick={() => setConfirmOpen(true)}
                leftIcon={<Upload className="size-4 stroke-current" aria-hidden />}
              >
                Загрузить каталог
              </Button>
            </div>
          </div>
        ) : null}

        {result ? (
          <Alert variant="info" className="mt-4">
            Каталог загружен: новых программ — {result.created.length}, изменено — {result.changed.length}, без
            изменений — {result.unchanged}
            {result.archiveMissing && result.missing.length > 0 ? `, в архив — ${result.missing.length}` : ''}
            {result.recordsRecomputed > 0 ? `; пересчитаны сроки записей обучения: ${result.recordsRecomputed}` : ''}.
          </Alert>
        ) : null}
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title="Загрузить каталог?"
        message={
          preview
            ? `Будет добавлено программ: ${preview.created.length}, изменено: ${preview.changed.length}` +
              (archiveMissing && preview.missing.length > 0 ? `, убрано в архив: ${preview.missing.length}` : '') +
              '. Поданные заказы не изменятся.'
            : undefined
        }
        buttons={[
          { label: busy ? 'Загрузка…' : 'Загрузить', variant: 'primary', disabled: busy, onClick: () => void apply() },
          { label: 'Отмена', variant: 'secondary', disabled: busy, onClick: () => setConfirmOpen(false) },
        ]}
        onClose={() => setConfirmOpen(false)}
        closeDisabled={busy}
      />
    </div>
  )
}
